import React, {FC, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {ColumnProps} from 'antd/lib/table';
import {InputProps, SearchProps} from 'antd/lib/input';
import {RouteChildrenProps} from 'react-router-dom';
import moment from 'moment';
import {Breadcrumb, Button, Col, Divider, Icon, Input, Popconfirm, Row, Spin, Table} from 'antd';

import {Document, getReadableMimeType} from '../../store/api/apiTypes';
import {
  del,
  DocumentsState,
  getDocumentCreateState,
  getDocumentDeleteState,
  getDocumentsListState,
  getDocumentsStructureState,
  getDocumentUpdateState,
  list,
  structure,
} from '../../store/actions/documents';
import {MainReducerState} from '../../store/reducers';
import {
  details as sectionDetail,
  getSectionDetailsState,
  getSectionsListState,
  list as listSections,
  SectionsState,
} from '../../store/actions/sections';
import {canManageFiles, constants} from '../../utils';
import DocumentDrawer from './DocumentDrawer';
import {CheckLoginStatusState, getUser} from '../../store/actions/auth';
import {getRoute, RoutePathName} from '../../routes';
import {content} from '../../store/api/documents';
import {AxiosResponse} from 'axios';

interface MatchParams {
  section?: string;
  year?: string;
}

interface DocumentsProps extends RouteChildrenProps<MatchParams> {
  creates: DocumentsState['create'];
  deletes: DocumentsState['delete'];
  sectionsState: SectionsState['list'];
  sectionState: SectionsState['details'];
  documentsState: DocumentsState['list'];
  structureState: DocumentsState['structure'];
  updates: DocumentsState['update'];
  user: CheckLoginStatusState['user'];
  getDocuments: typeof list.trigger;
  getStructure: typeof structure.trigger;
  getSections: typeof listSections.trigger;
  getSection: typeof sectionDetail.trigger;
  deleteDocument: typeof del.trigger;
}

const Documents: FC<DocumentsProps> = ({
                                         creates,
                                         deletes,
                                         deleteDocument,
                                         documentsState,
                                         getDocuments,
                                         getStructure,
                                         match,
                                         updates,
                                         user,
                                         sectionsState,
                                         sectionState,
                                         getSection,
                                       }) => {

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();
  const [selectedSection, setSelectedSection] = useState<string>();
  const [selectedYear, setSelectedYear] = useState<string>();
  const [search, setSearch] = useState<string>();

  useEffect(() => {
    getStructure();
    getDocuments({
      limit: constants.PAGE_SIZE,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (match && match.params) {
      const year: any = match.params.year ? match.params.year : undefined;
      const sec: any = match.params.section ? match.params.section : undefined;
      setSelectedYear(year);
      setSelectedSection(sec);
      getSection({id: sec});
    }
  }, [match]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params: any = {
      limit: constants.PAGE_SIZE,
    };
    if (search && search.length > 1) {
      params.searchString = search;
      params.throttling = 300;
    }
    if (selectedSection) {
      params.section = selectedSection;
    }
    if (selectedSection) {
      params.year = selectedYear;
    }
    getDocuments(params);
  }, [search, selectedYear, selectedSection]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params: any = {
      limit: constants.PAGE_SIZE,
    };
    if (creates.success || deletes.success || updates.success) {
      if (selectedYear) {
        params.year = selectedYear;
      }
      if (selectedSection) {
        params.section = selectedSection;
      }
      getStructure();
    }
    getDocuments(params);
  }, [creates.success, deletes.success, updates.success]); // eslint-disable-line react-hooks/exhaustive-deps

  const setEditMode = (id: string) => {
    setSelectedId(id);
    setIsFormVisible(true);
  };

  const onSearchChange: InputProps['onChange'] = (e) => {
    setSearch(e.target.value);
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    setSearch(value);
  };

  const onAdd = () => {
    setSelectedId(undefined);
    setIsFormVisible(true);
  };

  const onDeleteElement = (elem: Document) => {
    deleteDocument({id: elem.id});
  };

  const onDrawerClose = () => {
    setIsFormVisible(false);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const params: any = {
      limit: constants.PAGE_SIZE,
      page: pagination.current - 1,
      pageSize: pagination.pageSize,
      sortBy: sorter.field,
      sortOrder: sorter.order,
    };
    if (selectedYear) {
      params.year = selectedYear;
    }
    if (selectedSection) {
      params.section = selectedSection;
    }
    getDocuments(params);
  };

  const onOpenFile = (doc: Document) => {
    content({id: doc.id}).then((res) => {
      const downloadedFile = `data:application/${doc.type};base64,${res.toString()}`;
      const a = document.createElement('a');
      a.href = downloadedFile;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
    });
  };

  const rowKey = (item: Document) => `${item.id}`;
  const columns: Array<ColumnProps<Document>> = [
    {
      dataIndex: 'name',
      title: 'Nom',
      ellipsis: false,
      sorter: true,
      fixed: 'left',
      width: 200,
    },
    {
      dataIndex: 'description',
      title: 'Description',
      ellipsis: true,
      width: 200,
    },
    {
      dataIndex: 'type',
      title: 'Type',
      render: (record) => getReadableMimeType(record),
      ellipsis: true,
      sorter: true,
      width: 100,
    },
    {
      title: 'Rubrique',
      render: (record) => record.section ? record.section.short : '',
      ellipsis: true,
      width: 100,
    },
    {
      dataIndex: 'year',
      title: 'Année',
      ellipsis: true,
      sorter: true,
      width: 100,
    },
    {
      title: 'Date',
      render: (record) => (moment(record.createdAt).format('DD-MM-YY à HH:mm')),
      ellipsis: false,
      width: 100,
    },
    {
      title: 'Par',
      render: (record) => record.createdBy ? record.createdBy.firstName + record.createdBy.lastName : '',
      ellipsis: false,
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      ellipsis: false,
      fixed: 'right',
      render: (text, record) => (
        <>
          <Button
            icon="eye"
            className="list-action-button-edit"
            onClick={onOpenFile.bind(null, record)}
          />
          {canManageFiles(user) && (
            <>
              <Divider type="vertical"/>
              <Button
                icon="edit"
                className="list-action-button-edit"
                onClick={setEditMode.bind(null, record.id)}
              />
              <Divider type="vertical"/>
              <Popconfirm
                title="Voulez-vous vraiment supprimer?"
                onConfirm={onDeleteElement.bind(null, record)}
                okText="Oui"
                cancelText="Non"
              >
                <Button icon="delete" type="danger"/>
              </Popconfirm>
            </>
          )}
        </>
      ),
    },
  ];

  const section = sectionState.data;

  return (
    <>
      <Spin spinning={documentsState.loading}>
        {documentsState && documentsState.data && (
          <>
            <Row align="middle" gutter={[8, 40]}>
              <Col>
                <Breadcrumb>
                  <Breadcrumb.Item href={getRoute(RoutePathName.documents, {})}>
                    <Icon type="home"/>
                  </Breadcrumb.Item>
                  {section && section.parent && (
                    <Breadcrumb.Item href={getRoute(RoutePathName.documents, {section: section.parent.id})}>
                      <Icon type={section.parent.picto}/>
                      <span>{section.parent.name}</span>
                    </Breadcrumb.Item>
                  )}
                  {section && (
                    <Breadcrumb.Item href={getRoute(RoutePathName.documents, {section: section.id})}>
                      <Icon type={section.picto}/>
                      <span>{section.name}</span>
                    </Breadcrumb.Item>
                  )}
                  <Breadcrumb.Item>
                    {selectedYear && (<span>{selectedYear}</span>)}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
            </Row>
            <Row align="middle" gutter={[8, 40]}>
              <Col xs={8} sm={10} md={12} lg={14} xl={16}>
                <Input.Search
                  placeholder="Rechercher"
                  onChange={onSearchChange}
                  onSearch={onSearch}
                  size="small"
                />
              </Col>
              <Col xs={1} sm={4} md={4} lg={4} xl={4} push={4}>
                {canManageFiles(user) && (
                  <Button
                    type="primary"
                    icon="plus-circle"
                    onClick={onAdd}
                  >
                    Ajouter un document
                  </Button>
                )}
              </Col>
            </Row>
            <Row gutter={[8, 40]}>
              <Table<Document>
                scroll={{x: 1000}}
                rowKey={rowKey}
                columns={columns}
                loading={documentsState.loading}
                dataSource={documentsState.data.items}
                pagination={{
                  total: documentsState.data.totalCount,
                  current: documentsState.data.page + 1,
                  pageSize: documentsState.data.pageSize,
                }}
                onChange={handleTableChange}
              />
            </Row>

            <DocumentDrawer
              id={selectedId}
              isVisible={isFormVisible}
              onClose={onDrawerClose}
              section={section}
              year={selectedYear}
            />
          </>
        )}
      </Spin>
    </>
  );
};

const mapStateToProps = (state: MainReducerState) => ({
  creates: getDocumentCreateState(state),
  deletes: getDocumentDeleteState(state),
  documentsState: getDocumentsListState(state),
  structureState: getDocumentsStructureState(state),
  sectionsState: getSectionsListState(state),
  sectionState: getSectionDetailsState(state),
  updates: getDocumentUpdateState(state),
  user: getUser(state),
});

export default connect(
  mapStateToProps,
  {
    getDocuments: list.trigger,
    getStructure: structure.trigger,
    deleteDocument: del.trigger,
    getSections: listSections.trigger,
    getSection: sectionDetail.trigger,
  },
)(Documents);
